/**
 * The archive of ended battle sessions (collection `battles`, services/sessions.ts BattleDoc: the session's info and
 * its whole command log), with an in-memory index of their summaries and a retention policy.
 *
 * A stored battle holds a command log of up to a megabyte, and JsonFileStorage keeps every document in memory: an
 * archive without a limit grew without bound, and reading it with storage.list (the operator console's player card and
 * battle list, every account deletion) parsed every battle on the event loop, which held up the server for everyone
 * for seconds as it grew (the replays had the same problem: replays.ts). So:
 *  - the index of summaries (who played, mode, outcome, when) is built from the collection once per process (first
 *    use) and kept in step with every write of the collection (SessionService.finalize, the account deletion's
 *    anonymization, the pruning here), so lists and lookups never read the battles themselves;
 *  - `prune` (the core's sweep) deletes the battles that ended more than config.battles.keepMs ago (BATTLES_KEEP) and
 *    the oldest ones beyond config.battles.maxStored (BATTLES_MAX), at most PRUNE_PER_SWEEP per sweep. The owner's
 *    replay keeps the battle's log (replays.ts, up to REPLAYS_PER_PLAYER); session.get of a pruned battle answers
 *    NOT_FOUND 'session_not_found'.
 */
import type { PlayerId } from '../../../shared/protocol/model';
import { deleteOp } from '../storage';
import { type ServiceDeps, C } from './deps';
import type { BattleDoc } from './sessions';

/** What the index keeps of a stored battle. */
export interface BattleSummary {
  sessionId: string;
  mode: string;
  online: boolean;
  participants: { playerId: PlayerId; displayName: string }[];
  winnerTeam: number | null;
  verdict: string | null;
  reason: string | null;
  replayId: string | null;
  /** When it ended (0 for a document without an outcome: pruned first). */
  endedAt: number;
}

/** Battles deleted by one sweep at most (one batch of PRUNE_CHUNK at a time), so a sweep never runs long. */
export const PRUNE_PER_SWEEP = 1000;
const PRUNE_CHUNK = 100;

export function battleSummaryOf(doc: BattleDoc): BattleSummary | null {
  const info = doc?.info;
  if (typeof doc?.sessionId !== 'string' || !info || !Array.isArray(info.participants)) return null;
  return {
    sessionId: doc.sessionId,
    mode: String(info.mode),
    online: info.online !== null && info.online !== undefined,
    participants: info.participants.filter((p) => p && typeof p.playerId === 'string').map((p) => ({ playerId: p.playerId, displayName: String(p.displayName ?? '') })),
    winnerTeam: info.outcome?.result?.winnerTeam ?? null,
    verdict: info.outcome?.verdict ?? null,
    reason: info.outcome?.reason ?? null,
    replayId: info.outcome?.replayId ?? null,
    endedAt: typeof info.outcome?.endedAt === 'number' ? info.outcome.endedAt : 0,
  };
}

/** Newest first; battles of the same millisecond by id, descending. */
export function newestBattleFirst(a: BattleSummary, b: BattleSummary): number {
  return b.endedAt - a.endedAt || (a.sessionId < b.sessionId ? 1 : a.sessionId > b.sessionId ? -1 : 0);
}

export class BattleArchive {
  private index: Map<string, BattleSummary> | null = null;
  private byPlayer = new Map<PlayerId, Set<string>>();
  private loading: Promise<Map<string, BattleSummary>> | null = null;
  private pruning: Promise<number> | null = null;

  constructor(private readonly d: ServiceDeps) {}

  /** The index (see the module comment); the first call reads the collection once. */
  private summaries(): Promise<Map<string, BattleSummary>> {
    if (this.index) return Promise.resolve(this.index);
    this.loading ??= (async () => {
      const docs = await this.d.storage.list<BattleDoc>(C.battles);
      const index = new Map<string, BattleSummary>();
      this.index = index;
      for (const doc of docs) {
        const s = battleSummaryOf(doc);
        if (s) this.add(s);
      }
      return index;
    })().finally(() => (this.loading = null));
    return this.loading;
  }

  private add(s: BattleSummary): void {
    this.drop(s.sessionId);
    this.index!.set(s.sessionId, s);
    for (const p of s.participants) {
      let set = this.byPlayer.get(p.playerId);
      if (!set) this.byPlayer.set(p.playerId, (set = new Set()));
      set.add(s.sessionId);
    }
  }

  private drop(sessionId: string): void {
    const old = this.index!.get(sessionId);
    if (!old) return;
    this.index!.delete(sessionId);
    for (const p of old.participants) {
      const set = this.byPlayer.get(p.playerId);
      set?.delete(sessionId);
      if (set?.size === 0) this.byPlayer.delete(p.playerId);
    }
  }

  /** After a write of the battle's document (it was stored or replaced): the index shows it. */
  async stored(doc: BattleDoc): Promise<void> {
    const s = battleSummaryOf(doc);
    await this.summaries();
    if (s) this.add(s);
  }

  /** After the battle's document was deleted. */
  async removed(sessionId: string): Promise<void> {
    await this.summaries();
    this.drop(sessionId);
  }

  /** Every stored battle, newest first. */
  async all(): Promise<BattleSummary[]> {
    return [...(await this.summaries()).values()].sort(newestBattleFirst);
  }

  /** The stored battles the player took part in, newest first. */
  async ofPlayer(playerId: PlayerId): Promise<BattleSummary[]> {
    const index = await this.summaries();
    return [...(this.byPlayer.get(playerId) ?? [])]
      .map((id) => index.get(id))
      .filter((s): s is BattleSummary => s !== undefined)
      .sort(newestBattleFirst);
  }

  async size(): Promise<number> {
    return (await this.summaries()).size;
  }

  /**
   * Deletes the battles past the retention (see the module comment); returns how many. One run at a time; a run
   * that fails (storage) leaves the rest for the next sweep.
   */
  prune(now: number): Promise<number> {
    this.pruning ??= this.runPrune(now).finally(() => (this.pruning = null));
    return this.pruning;
  }

  private async runPrune(now: number): Promise<number> {
    const { keepMs, maxStored } = this.d.config.battles;
    const oldestFirst = (await this.all()).reverse();
    const cutoff = now - keepMs;
    const over = Math.max(0, oldestFirst.length - maxStored);
    const due = oldestFirst.filter((s, i) => i < over || s.endedAt < cutoff).slice(0, PRUNE_PER_SWEEP);
    let pruned = 0;
    for (let i = 0; i < due.length; i += PRUNE_CHUNK) {
      const chunk = due.slice(i, i + PRUNE_CHUNK);
      await this.d.storage.batch(chunk.map((s) => deleteOp(C.battles, s.sessionId)));
      for (const s of chunk) this.drop(s.sessionId);
      pruned += chunk.length;
    }
    if (pruned > 0) this.d.logger.info('stored battles pruned', { pruned, kept: this.index?.size ?? 0, keepDays: Math.round(keepMs / 86_400_000), maxStored });
    return pruned;
  }
}
